import { useState, useEffect, useSyncExternalStore } from "react";

/**
 * Returns true when the viewport is currently in landscape orientation.
 *
 * Tracks the `(orientation: landscape)` media query and also listens to
 * `orientationchange` / `resize` so we stay correct across the various ways a
 * device can report a rotation (mirrors the resize handling already used in
 * App.js and the orientation read in useDeviceDetection.js).
 */
export default function useIsLandscape() {
  const getMatch = () =>
    typeof window !== "undefined" &&
    window.matchMedia("(orientation: landscape)").matches;

  const [landscape, setLandscape] = useState(getMatch);

  useEffect(() => {
    const mq = window.matchMedia("(orientation: landscape)");
    const onChange = () => setLandscape(mq.matches);

    // Safari < 14 only supports the deprecated addListener API.
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
    } else if (mq.addListener) {
      mq.addListener(onChange);
    }
    window.addEventListener("orientationchange", onChange);
    window.addEventListener("resize", onChange);

    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener("change", onChange);
      } else if (mq.removeListener) {
        mq.removeListener(onChange);
      }
      window.removeEventListener("orientationchange", onChange);
      window.removeEventListener("resize", onChange);
    };
  }, []);

  return landscape;
}

/**
 * Current viewport orientation as a telemetry value, safe to call from
 * anywhere (event emitters, module scope): "landscape" | "portrait" |
 * "unknown" when there is no window (tests, SSR).
 */
export function currentOrientation() {
  try {
    if (typeof window === "undefined" || !window.matchMedia) return "unknown";
    return window.matchMedia("(orientation: landscape)").matches ? "landscape" : "portrait";
  } catch {
    return "unknown";
  }
}

// ── Landscape-capable screens ──
//
// The app-wide OrientationWarning asks phone users to rotate back to
// portrait because most of the journey doesn't lay out sideways. Screens
// that DO support landscape (the Veratad ID document capture) register
// themselves here while mounted so the warning stands down instead of
// covering a layout that was designed for the rotation.
//
// A counter rather than a boolean so nested / overlapping registrations
// (e.g. a capture screen that briefly mounts twice during a phase change)
// can't leave the warning stuck off or flash it back on.
let landscapeSupportCount = 0;
const listeners = new Set();

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};
const getSnapshot = () => landscapeSupportCount > 0;
const emit = () => listeners.forEach((l) => l());

/** True while at least one mounted screen has declared landscape support. */
export function useLandscapeSupported() {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/**
 * Declare that the calling screen lays out correctly in landscape for as
 * long as it is mounted (and `active` is true).
 */
export function useDeclareLandscapeSupport(active = true) {
  useEffect(() => {
    if (!active) return undefined;
    landscapeSupportCount += 1;
    emit();
    return () => {
      landscapeSupportCount -= 1;
      emit();
    };
  }, [active]);
}

// ── Portrait-only screens ──
//
// The inverse: camera screens built only for portrait (ID doc v1, the
// third-party SDK captures) declare it while mounted, and the rotate
// prompt shows on them even when a landscape preview flag has it
// switched off everywhere else (see OrientationWarning).
let portraitOnlyCount = 0;
const getPortraitOnlySnapshot = () => portraitOnlyCount > 0;

/** True while at least one mounted screen has declared it is portrait only. */
export function usePortraitOnly() {
  return useSyncExternalStore(subscribe, getPortraitOnlySnapshot, getPortraitOnlySnapshot);
}

/** Declare that the calling screen does not lay out in landscape. */
export function useDeclarePortraitOnly(active = true) {
  useEffect(() => {
    if (!active) return undefined;
    portraitOnlyCount += 1;
    emit();
    return () => {
      portraitOnlyCount -= 1;
      emit();
    };
  }, [active]);
}
